<script setup lang="ts">
import type { TextSettings, ThemeSettings } from '~/stores/auth'

const props = defineProps<{
  views: number
  textSettings: TextSettings
  themeSettings: ThemeSettings
}>()

// Parse primary color hex to RGB for glow effects (matches branding card pattern)
const primaryRgb = computed(() => {
  const hex = props.themeSettings.primaryColor.replace('#', '')
  return [
    parseInt(hex.substring(0, 2), 16),
    parseInt(hex.substring(2, 4), 16),
    parseInt(hex.substring(4, 6), 16)
  ]
})

const visible = ref(false)
onMounted(() => {
  visible.value = true
})

const containerStyle = computed(() => ({
  background: `linear-gradient(135deg, rgba(${primaryRgb.value.join(',')}, 0.12) 0%, rgba(${primaryRgb.value.join(',')}, 0.04) 100%)`,
  borderColor: `rgba(${primaryRgb.value.join(',')}, 0.2)`,
  boxShadow: `0 0 20px rgba(${primaryRgb.value.join(',')}, 0.15), 0 4px 12px rgba(0, 0, 0, 0.25)`
}))

const iconColor = computed(() => `color-mix(in srgb, ${props.themeSettings.primaryColor} 60%, white)`)
</script>

<template>
  <!-- Desktop only - WidgetCorner handles positioning and desktop visibility -->
  <Transition name="views-slide">
    <div
      v-if="visible"
      class="flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-md transition-all duration-300 hover:scale-105"
      :style="containerStyle"
    >
      <UIcon
        name="i-lucide-eye"
        class="size-3.5 shrink-0"
        :style="{ color: iconColor }"
      />
      <span
        class="text-xs font-bold tracking-wide tabular-nums"
        :style="{ color: iconColor }"
      >
        {{ views.toLocaleString() }}
      </span>
      <span class="text-[11px] font-medium text-white/50">
        views
      </span>
    </div>
  </Transition>
</template>

<style scoped>
.views-slide-enter-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 1.2s;
}
.views-slide-leave-active {
  transition: all 0.3s ease-in;
}
.views-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.views-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
